import { useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	ANALYTICS_CHANNEL,
	ANALYTICS_SOURCE,
	ANALYTICS_TAG,
	NAV_CONTAINER,
	NAV_LAYER,
	NAV_VERSION,
	type UniversalCreateSourceProduct,
} from '@atlassian/universal-create-common';
import type { PersonalizeMode } from '@atlassian/universal-create-common/bin-param';

import { useMenuControl } from '../context';
import type { SourceProduct, SuggestionConfigEnabled } from '../types';

export interface MenuContentViewedAttributes {
	crossAppSuggestionsEnabled: boolean;
	nativeSections: Array<{
		itemAtis: string[];
		itemCount: number;
		sectionHeading?: string;
		sectionIndex: number;
		sectionType: string;
	}>;
	totalNativeItems: number;
	totalNativeSections: number;
}

export interface MenuItemClickedAttributes {
	ati: string;
	cloudId?: string;
	sourceProduct?: UniversalCreateSourceProduct;
	suggestionProduct?: UniversalCreateSourceProduct;
}

export interface FormButtonClickedAttributes {
	action?: string;
	actionSubjectId: string;
	itemAti: string;
	itemProduct: UniversalCreateSourceProduct;
}

export interface SuggestionMenuViewedAttributes {
	suggestedItems: string[];
	suggestionsCount: number;
}

export interface CreateButtonClickedAttributes {
	itemState: 'expanded' | 'collapsed';
	version?: 'v1' | 'v2';
}

export interface NewMenuItemClickedAttributes {
	ati: string; // Atlassian Type Identifier of the item
	groupItemProduct: UniversalCreateSourceProduct; // Product item is natively associated with
	groupItemsCount: number; // Number of items in the section
	groupItemsIndex: number; // Position of the item in the section
	isRightClick: boolean; // True if event triggered by right click opening context menu
	menuItemIndex: number; // Position of the item in the entire native sections part of menu (not just section)
	menuItemsCount: number; // Total number of items in all native sections of menu
}

export interface NewMenuItemClickedAttributesV2 {
	ati: string; // Atlassian Type Identifier of the item
	groupItemProduct: SourceProduct; // Product item is natively associated with
	groupItemsCount: number; // Number of items in the section
	groupItemsIndex: number; // Position of the item in the section
	isNativeItem: boolean;
	isRightClick: boolean; // True if event triggered by right click opening context menu
	itemType: 'native' | 'integration' | 'iframe' | 'link' | 'platform';
	menuItemIndex: number; // Position of the item in the entire native sections part of menu (not just section)
	menuItemsCount: number; // Total number of items in all native sections of menu
	sectionType: 'appDefinedSection' | 'suggestionSection';
}

export interface NewNativeMenuViewedAttributes_old {
	crossAppSuggestionsEnabled: boolean;
	nativeMenuGroups: Array<{
		groupHeading: string;
		groupIndex: number; // Index of section out of all sections
		groupItemCount: number; // total number of items in the section
		itemAtis: string[];
		showMoreButton: boolean; // true if items in section have been truncated with show more button
	}>;
	totalGroups: number; // Total count of native sections in the menu
	totalNativeItems: number; // Total count of items in all native sections
}

export interface NewNativeMenuViewedAttributes {
	crossAppSuggestionsEnabled: boolean;
	nativeGroupCount: number; // Total count of native sections in the menu
	nativeItemCount: number; // Total count of items in all native sections
	nativeMenuGroups: Array<{
		groupHeading: string;
		groupIndex: number; // Index of section out of all sections
		groupItemAtisList: string[];
		groupItemCount: number; // total number of items in the section
		inMoreItemAtisList: string[];
		inMoreItemCount: number;
		showMoreButton: boolean; // true if items in section have been truncated with show more button
	}>;
}

export interface NewSuggestionsMenuViewedAttributes_old {
	crossAppSuggestionsEnabled: boolean;
	suggestionMenuGroup: {
		displayModeCompact: SuggestionConfigEnabled['compact']; // Configuration of whether 'Suggestion/s' heading is hidden or visible
		itemAtis: string[];
		showMoreButton: boolean; // true if items in section have been truncated with show more button
		suggestionLocation: SuggestionConfigEnabled['location']; // Whether suggestions are shown at start (top) or end of menu (below native items)
	};
	totalSuggestionItems: number; // Total count of items in suggestions section
}

export interface NewMenuViewedAttributes {
	crossAppCreationEnabled: boolean;
	sectionMetadata: Array<
		{
			groupHeading?: string;
			groupIndex: number; // Index of section out of all sections
			groupItemCount: number; // total number of items in the section
			itemAtis: string[];
			itemTypes: string[];
			showMoreButton: boolean; // true if items in section have been truncated with show more button
		} & (
			| {
					personalize: PersonalizeMode;
					type: 'appDefinedSection';
			  }
			| { type: 'suggestionSection' }
		)
	>;
	totalCrossAppItems: number;
	totalNativeItems: number;
	totalSections: number;
}

export interface NewSuggestionsMenuViewedAttributes {
	crossAppSuggestionsEnabled: boolean;
	suggestionGroupList: Array<{
		groupHeading: string;
		groupIndex: number; // Index of section out of all sections
		groupItemAtisList: string[];
		groupItemCount: number; // total number of items in the section
		inMoreItemAtisList: string[];
		inMoreItemCount: number;
		showMoreButton: boolean; // true if items in section have been truncated with show more button
	}>;
	suggestionsGroupCount: number; // Total count of suggestion sections in the menu
	suggestionsItemCount: number; // Total count of items in suggestions section
}

export interface UniversalCreateAnalyticsEvents {
	// Create button click events
	fireCreateButtonClicked: (attributes: CreateButtonClickedAttributes) => void;

	// Modal control analytic events
	fireFormControlAttributes: (attributes: FormButtonClickedAttributes) => void;

	// Universal create menu expanded or collapsed via any exit action
	fireMenuStateChanged: (attributes: CreateButtonClickedAttributes) => void;

	// Combined Menu item click events
	fireNewMenuItemClicked: (attributes: NewMenuItemClickedAttributesV2) => void;

	// Menu items viewed events
	fireNewMenuItemsViewed: (attributes: NewMenuViewedAttributes) => void;

	// Native Menu item click events
	fireNewNativeMenuItemClicked: (attributes: NewMenuItemClickedAttributes) => void;

	// Native Menu items viewed events
	fireNewNativeMenuViewed: (attributes: NewNativeMenuViewedAttributes) => void;

	fireNewNativeMenuViewed_old: (attributes: NewNativeMenuViewedAttributes_old) => void;

	// Suggestions Menu item click events
	fireNewSuggestionsMenuItemClicked: (attributes: NewMenuItemClickedAttributes) => void;

	// Suggestions Menu items viewed events
	fireNewSuggestionsMenuViewed: (attributes: NewSuggestionsMenuViewedAttributes) => void;

	fireNewSuggestionsMenuViewed_old: (attributes: NewSuggestionsMenuViewedAttributes_old) => void;
}

/**
 * Custom hook for Universal Create analytics events
 * Provides type-safe, consistent analytics event firing across all UC components
 */
export function useUniversalCreateAnalytics(): UniversalCreateAnalyticsEvents {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { product, subProduct } = useMenuControl();

	// Provide fallback for product if undefined
	const safeProduct = product || 'unknown';

	const baseAttributes = useMemo(
		() => ({
			navLayer: NAV_LAYER,
			navContainer: NAV_CONTAINER,
			navVersion: NAV_VERSION,
			product: safeProduct,
			subProduct,
		}),
		[safeProduct, subProduct],
	);

	const topLevelAttributes = useMemo(
		() => ({
			actionSubject: 'universalCreate',
			eventType: 'ui',
			source: ANALYTICS_SOURCE,
			tags: [ANALYTICS_TAG],
		}),
		[],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88038
	const fireCreateButtonClicked = useCallback(
		(attributes: CreateButtonClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'clicked',
				actionSubjectId: 'universalCreateButton',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88332
	const fireNewNativeMenuItemClicked = useCallback(
		(attributes: NewMenuItemClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'clicked',
				actionSubjectId: 'universalCreateNativeMenuItem',
				attributes: {
					group: 'native',
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88486
	const fireNewSuggestionsMenuItemClicked = useCallback(
		(attributes: NewMenuItemClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'clicked',
				actionSubjectId: 'universalCreateSuggestionsMenuItem',
				attributes: {
					group: 'suggestions',
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88295
	const fireNewMenuItemClicked = useCallback(
		(attributes: NewMenuItemClickedAttributesV2) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'clicked',
				actionSubjectId: 'universalCreateMenuItem',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88599
	const fireNewNativeMenuViewed_old = useCallback(
		(attributes: NewNativeMenuViewedAttributes_old) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'viewed',
				actionSubjectId: 'universalCreateNativeMenu',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88599
	const fireNewNativeMenuViewed = useCallback(
		(attributes: NewNativeMenuViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'viewed',
				actionSubjectId: 'universalCreateNativeMenu',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88724
	const fireNewSuggestionsMenuViewed_old = useCallback(
		(attributes: NewSuggestionsMenuViewedAttributes_old) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'viewed',
				actionSubjectId: 'universalCreateSuggestionsMenu',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/88724
	const fireNewSuggestionsMenuViewed = useCallback(
		(attributes: NewSuggestionsMenuViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'viewed',
				actionSubjectId: 'universalCreateSuggestionsMenu',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92169
	const fireNewMenuItemsViewed = useCallback(
		(attributes: NewMenuViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'viewed',
				actionSubjectId: 'universalCreateMenuSection',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/89698
	const fireMenuStateChanged = useCallback(
		(attributes: CreateButtonClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				action: 'changed',
				actionSubjectId: 'universalCreateMenuState',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes, baseAttributes],
	);

	// createModal viewed Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90539
	// createModalBackButton clicked Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90540
	// createModalCancelButton clicked Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90541
	// createModalCreateButton clicked Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/90542
	const fireFormControlAttributes = useCallback(
		({ actionSubjectId, action = 'clicked', ...attributes }: FormButtonClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				actionSubject: 'universalCreate',
				action,
				actionSubjectId,
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent, baseAttributes, topLevelAttributes],
	);

	return {
		fireCreateButtonClicked, // new
		fireNewNativeMenuItemClicked, // new ==> merging
		fireNewSuggestionsMenuItemClicked, // new ==> merging
		fireNewMenuItemClicked, // new ==> combining native and suggestions
		fireNewNativeMenuViewed_old, // new
		fireNewNativeMenuViewed, // new + updated
		fireNewSuggestionsMenuViewed_old, // new
		fireNewSuggestionsMenuViewed, // new + updated
		fireNewMenuItemsViewed, // new
		fireMenuStateChanged, // new
		fireFormControlAttributes,
	};
}
